import {
  Calendar,
  Home,
  Inbox,
  Search,
  Settings,
  Users,
  Scale,
  User,
  HandCoins,
  LogOut,
  Archive,
  ChevronDown,
  DollarSign,
} from "lucide-react";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";

import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarSeparator,
  SidebarTrigger,
} from "@/components/ui/sidebar";
import { Button } from "./button";
import { useAuthContext } from "@/context/AuthContext";
import { Link, useLocation } from "react-router-dom";
import { useCurrentUser } from "@/context/UserContext";
import { Separator } from "./separator";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "./accordion";
import { useState } from "react";
import ChangePasswordDialog from "@/admin/users-page/indiv-user-page/ChangePasswordDialog";

// Menu items.
const adminItems = [
  {
    title: "Home",
    url: "",
    icon: Home,
  },
  {
    title: "Users",
    url: "users",
    icon: Users,
  },
  {
    title: "Leave Balance",
    url: "leave-balance",
    icon: Scale

  }
];

const payrollItems = [
  {
    title: "Process",
    url: "process-pay",
  },
  {
    title: "Records",
    url: "pay-records",
  },
];
const staffItems = [
  {
    title: "Home",
    url: "",
    icon: Home,
  },
  {
    title: "Pay",
    url: "payslips",
    icon: HandCoins,
  },
  {
    title: "Profile",
    url: "profile",
    icon: User,
  },
];
export function AdminAppSidebar() {
  const { currentUser } = useCurrentUser();
  const { logout } = useAuthContext();
  const location = useLocation();

  const [isChangePwOpen, setIsChangePwOpen] = useState(false);
  function isActive(url: string) {
    return (
      location.pathname === `/admin/${url}` ||
      (url === "" && location.pathname === "/admin")
    );
  }

  return (
    <Sidebar collapsible="icon" variant="sidebar" className="border-r">
      <SidebarContent className="p-2 h-full">
        <SidebarGroup className="h-full flex flex-col">
          <SidebarGroupLabel>
            <div className="flex items-center w-full">
              <div className="group-data-[collapsible=icon]:block hidden text-center w-full font-semibold text-lg">
                XP
              </div>
              <div className="group-data-[collapsible=icon]:hidden block text-lg font-semibold">
                Xorex@HR
              </div>
            </div>
          </SidebarGroupLabel>
          <SidebarGroupContent className="flex flex-col flex-1">
            <div className="text-base font-medium pl-2 mb-3 group-data-[collapsible=icon]:hidden">
              <div>Welcome,</div>
              <div className="lowercase">
                {currentUser?.fullName}
              </div>
            </div>
            <Separator className="my-4 group-data-[collapsible=icon]:hidden" />
            <SidebarMenu className="flex-1 flex flex-col">
              <div className="flex-1 gap-2 flex flex-col">
                {adminItems.map((item) => (
                  <SidebarMenuItem key={item.title}>
                    <SidebarMenuButton asChild tooltip={item.title}>
                      <Link
                        to={`/admin/${item.url}`}
                        className={`${isActive(item.url)
                          ? "font-bold bg-gray-100 dark:bg-gray-800"
                          : "hover:bg-gray-50 dark:hover:bg-gray-800"
                          } transition-colors flex items-center ${item.title === "Home"
                            ? "justify-start"
                            : "group-data-[collapsible=icon]:justify-center"
                          }`}
                      >
                        <item.icon className="flex-shrink-0 h-5 w-5" />
                        <span className="group-data-[collapsible=icon]:hidden ml-3">
                          {item.title}
                        </span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}
                {/* Collapsible Payroll Section */}
                <Collapsible defaultOpen className="group">
                  <SidebarMenuItem>
                    <SidebarMenuButton asChild tooltip="Pay">
                      <CollapsibleTrigger className="flex items-center w-full rounded-md cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 group-data-[collapsible=icon]:justify-center">
                        <DollarSign className="flex-shrink-0 h-5 w-5" />
                        <span className="group-data-[collapsible=icon]:hidden ml-3">
                          Pay
                        </span>
                        <ChevronDown className="ml-auto transition-transform group-data-[state=open]:rotate-180 group-data-[collapsible=icon]:hidden h-4 w-4" />
                      </CollapsibleTrigger>
                    </SidebarMenuButton>
                  </SidebarMenuItem>

                  <CollapsibleContent className="group-data-[collapsible=icon]:hidden">
                    {payrollItems.map((item) => (
                      <SidebarMenuSub key={item.title} className="pr-0 mr-0">
                        <SidebarMenuSubItem key={item.title} className="w-full">
                          <SidebarMenuSubButton asChild>
                            <Link
                              to={`/admin/${item.url}`}
                              className={`${isActive(item.url)
                                ? "font-bold bg-gray-100 dark:bg-gray-800"
                                : "hover:bg-gray-50 dark:hover:bg-gray-800"
                                } transition-colors w-full flex items-center`}
                            >
                              <span>{item.title}</span>
                            </Link>
                          </SidebarMenuSubButton>
                        </SidebarMenuSubItem>
                      </SidebarMenuSub>
                    ))}
                  </CollapsibleContent>
                </Collapsible>

              </div>

              <div className="mt-auto mb-4 gap-4 flex flex-col">
                <Button
                  variant="outline"
                  type="button"
                  onClick={() => setIsChangePwOpen(true)}
                >
                  Change Password
                </Button>
                <SidebarSeparator />
                <SidebarMenuItem>
                  <SidebarMenuButton asChild tooltip="Logout">
                    <button
                      onClick={logout}
                      className="flex items-center w-full group-data-[collapsible=icon]:justify-center"
                    >
                      <LogOut className="flex-shrink-0 h-5 w-5" />
                      <span className="group-data-[collapsible=icon]:hidden ml-3">
                        Logout
                      </span>
                    </button>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              </div>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
      <ChangePasswordDialog
        isOpen={isChangePwOpen}
        setIsOpen={setIsChangePwOpen}
        userId={currentUser?.id as number}
      />
    </Sidebar>
  );
}

export function StaffAppSidebar() {
  const [isChangePwOpen, setIsChangePwOpen] = useState(false);
  const { isAuthenticated, logout } = useAuthContext();
  const location = useLocation();
  const { currentUser } = useCurrentUser();

  function isActive(url: string, item: any) {
    if (item.url === "" && location.pathname === "/staff") {
      return true;
    }
    return location.pathname === `/staff/${item.url}`;
  }

  return (
    <Sidebar collapsible="icon" variant="sidebar" className="border-r">
      <SidebarContent className="p-2 h-full">
        <SidebarGroup className="h-full flex flex-col">
          <SidebarGroupLabel>
            <div className="flex items-center w-full">
              <div className="group-data-[collapsible=icon]:block hidden text-center w-full font-semibold text-lg">
                XP
              </div>
              <div className="group-data-[collapsible=icon]:hidden block text-lg font-semibold">
                Xorex@HR
              </div>
            </div>
          </SidebarGroupLabel>
          <SidebarGroupContent className="flex flex-col flex-1">
            <div className="text-base font-medium pl-2 mb-3 group-data-[collapsible=icon]:hidden">
              <div>Welcome,</div>
              <div className="lowercase">
                {currentUser?.fullName}
              </div>
            </div>
            <Separator className="my-4 group-data-[collapsible=icon]:hidden" />
            <SidebarMenu className="flex-1 flex flex-col">
              <div className="flex-1 gap-2 flex flex-col">
                {staffItems.map((item) => (
                  <SidebarMenuItem key={item.title}>
                    <SidebarMenuButton asChild tooltip={item.title}>
                      <Link
                        to={`/staff/${item.url}`}
                        className={`${isActive(item.url, item)
                          ? "font-bold bg-gray-100 dark:bg-gray-800"
                          : "hover:bg-gray-50 dark:hover:bg-gray-800"
                          } transition-colors flex items-center ${item.title === "Home"
                            ? "justify-start"
                            : "group-data-[collapsible=icon]:justify-center"
                          }`}
                      >
                        <item.icon className="flex-shrink-0 h-5 w-5" />
                        <span className="group-data-[collapsible=icon]:hidden ml-3">
                          {item.title}
                        </span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}
              </div>

              <div className="mt-auto mb-4 gap-4 flex flex-col">
                <Button
                  variant="outline"
                  type="button"
                  onClick={() => setIsChangePwOpen(true)}
                >
                  Change Password
                </Button>
                <SidebarSeparator />
                <SidebarMenuItem>
                  <SidebarMenuButton asChild tooltip="Logout">
                    <button
                      onClick={logout}
                      className="flex items-center w-full group-data-[collapsible=icon]:justify-center"
                    >
                      <LogOut className="flex-shrink-0 h-5 w-5" />
                      <span className="group-data-[collapsible=icon]:hidden ml-3">
                        Logout
                      </span>
                    </button>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              </div>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
      <ChangePasswordDialog
        isOpen={isChangePwOpen}
        setIsOpen={setIsChangePwOpen}
        userId={currentUser?.id as number}
      />
    </Sidebar>
  );
}
